import { Typography } from "antd";
import { AnimatePresence, motion } from "motion/react";

import { AILoadingIndicator } from "../AILoadingIndicator";
import { AIRetry } from "../AIRetry";
import { Alert } from "../Alert";
import { renderMarkdown } from "../Markdown";

import * as theme from "./AIPromptResponse.css";
import {
    AIPromptResponseProps,
    AIUserPromptAndResponse,
} from "./AIPromptResponse.types";
import { AIPromptResponseButtonPanel } from "./AIPromptResponseButtonPanel";

const { Text } = Typography;

function getStatus(promptAndResponse: AIUserPromptAndResponse): string {
    if (promptAndResponse.error) {
        return "error";
    }
    if (!promptAndResponse.result) {
        return "loading";
    }
    return "result";
}

export function AIPromptResponse({
    userPromptsAndResponses = [],
    onRevertClick,
    loadingLabel,
    copiedTooltipLabel,
    copyTooltipLabel,
    revertTooltipLabel,
    emptyLabel,
    errorLabel,
    onRetryClick,
    retryLabel,
    onInsertClick,
    insertLabel,
    onStateChanged,
}: AIPromptResponseProps) {
    return (
        <div className={theme.root}>
            <AnimatePresence>
                {userPromptsAndResponses.map((item, index) => {
                    const isLast = index === userPromptsAndResponses.length - 1;
                    return (
                        <AIPromptResponseItem
                            key={item.id}
                            isLast={isLast}
                            userPromptAndResponse={item}
                            loadingLabel={loadingLabel}
                            copiedTooltipLabel={copiedTooltipLabel}
                            copyTooltipLabel={copyTooltipLabel}
                            revertTooltipLabel={revertTooltipLabel}
                            emptyLabel={emptyLabel}
                            onRevertClick={onRevertClick}
                            errorLabel={errorLabel}
                            onRetryClick={onRetryClick}
                            retryLabel={retryLabel}
                            onInsertClick={onInsertClick}
                            insertLabel={insertLabel}
                            onStateChanged={onStateChanged}
                        />
                    );
                })}
            </AnimatePresence>
        </div>
    );
}

interface AIPromptResponseItemProps {
    isLast: boolean;
    userPromptAndResponse: AIUserPromptAndResponse;
    onRevertClick: (id: string) => void;
    loadingLabel: string;
    copiedTooltipLabel: string;
    copyTooltipLabel: string;
    revertTooltipLabel: string;
    emptyLabel: string;
    errorLabel: string;
    retryLabel: string;
    onRetryClick: (id: string) => void;
    onInsertClick: (id: string) => void;
    insertLabel: string;
    onStateChanged?: () => void;
}

function AIPromptResponseItem({
    isLast,
    onRevertClick,
    loadingLabel,
    copiedTooltipLabel,
    copyTooltipLabel,
    revertTooltipLabel,
    userPromptAndResponse,
    emptyLabel,
    errorLabel,
    retryLabel,
    onRetryClick,
    onInsertClick,
    insertLabel,
    onStateChanged,
}: AIPromptResponseItemProps) {
    const { result, error, id, userPrompt } = userPromptAndResponse;
    const status = getStatus(userPromptAndResponse);

    let content;
    if (error) {
        content = (
            <AIRetry
                message={errorLabel}
                retryLabel={retryLabel}
                onRetry={() => onRetryClick(id)}
                variant="fullWidth"
            />
        );
    } else if (!result) {
        content = <AILoadingIndicator label={loadingLabel} />;
    } else if (result.kind === "empty") {
        content = (
            <>
                <Alert type="info" message={emptyLabel} />
                <AIPromptResponseButtonPanel
                    onRevertClick={isLast ? () => onRevertClick(id) : undefined}
                    revertTooltipLabel={revertTooltipLabel}
                />
            </>
        );
    } else {
        content = (
            <AIPromptResponseContent
                response={result.response}
                onRevertClick={isLast ? () => onRevertClick(id) : undefined}
                copiedTooltipLabel={copiedTooltipLabel}
                copyTooltipLabel={copyTooltipLabel}
                revertTooltipLabel={revertTooltipLabel}
                onInsertClick={() => onInsertClick(id)}
                insertLabel={insertLabel}
            />
        );
    }

    return (
        <motion.div
            className={theme.gapContainer}
            initial={{ opacity: 0, height: 0, overflow: "hidden" }}
            animate={{ opacity: 1, height: "auto", overflow: "visible" }}
            exit={{ opacity: 0, height: 0, overflow: "hidden" }}
            transition={{ duration: 0.3 }}
        >
            <div className={theme.userPromptRoot}>
                <Text className={theme.userPromptText}>{userPrompt}</Text>
            </div>
            <AnimatePresence mode="wait">
                <motion.div
                    key={status}
                    className={theme.gapContainer}
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -8 }}
                    transition={{ duration: 0.3 }}
                    onAnimationStart={onStateChanged}
                    onAnimationComplete={onStateChanged}
                >
                    {content}
                </motion.div>
            </AnimatePresence>
        </motion.div>
    );
}

interface AIPromptResponseContentProps {
    response: string;
    copiedTooltipLabel: string;
    copyTooltipLabel: string;
    revertTooltipLabel: string;
    onRevertClick?: () => void;
    onInsertClick: () => void;
    insertLabel: string;
}
function AIPromptResponseContent({
    response,
    copiedTooltipLabel,
    copyTooltipLabel,
    revertTooltipLabel,
    onRevertClick,
    onInsertClick,
    insertLabel,
}: AIPromptResponseContentProps) {
    const handleCopyClick = () => {
        navigator.clipboard.writeText(response);
    };
    return (
        <>
            <div>
                <Text>{renderMarkdown(response)}</Text>
            </div>
            <AIPromptResponseButtonPanel
                onCopyClick={handleCopyClick}
                onRevertClick={onRevertClick}
                copiedTooltipLabel={copiedTooltipLabel}
                copyTooltipLabel={copyTooltipLabel}
                revertTooltipLabel={revertTooltipLabel}
                onInsertClick={onInsertClick}
                insertLabel={insertLabel}
            />
        </>
    );
}
